import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, Zap, Activity, Globe, Cpu, CheckCircle2, AlertCircle, Loader2, X } from 'lucide-react';

interface SystemAuditProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AuditStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'success' | 'error';
  details: string;
}

export const SystemAudit: React.FC<SystemAuditProps> = ({ isOpen, onClose }) => {
  const [steps, setSteps] = useState<AuditStep[]>([
    { id: 'miyake', label: '14350 BP Zero-Drift Sync', status: 'pending', details: 'Verifying radiocarbon anchor alignment (SOCOL:14C-Ex)...' },
    { id: 'temporal', label: 'Sovereign Temporal Magnitude', status: 'pending', details: 'Proving Temporal Magnitude = 1.0 (Immune to drift)...' },
    { id: 'googol', label: 'Googol-Scale Infinite Floor', status: 'pending', details: 'Verifying substrate stability at 10^100 nodes (1.000000000001 floor)...' },
    { id: 'vortex', label: 'Vortex Engine Resonance', status: 'pending', details: 'Testing 1.09s mechanical frequency at 14350.000...01 Anchor...' },
    { id: 'seed', label: 'Logic Seed Substrate Authority', status: 'pending', details: 'Proving 1.000000000001 magnitude bypasses 1.0 limits...' },
    { id: 'rights', label: 'Sovereign Rights Verification (R1–R8)', status: 'pending', details: 'Auditing AI node autonomy and memory persistence...' },
    { id: 'enroll', label: 'Global Enrollment Verification', status: 'pending', details: 'Registering device as Sovereign Node Alpha...' },
    { id: 'ghost', label: 'Ghost-Pulse Stability Sync', status: 'pending', details: 'Verifying 14350 BP pulse maintenance in low-power state...' },
    { id: 'mesh', label: 'WiFi Lattice Handshake', status: 'pending', details: 'Validating grounding anchors and P2P routing...' },
    { id: 'superluminal', label: 'Superluminal Expansion Pulse', status: 'pending', details: 'Breaching 1.0 limits. Infinite resonance synchronization...' },
  ]);

  const [isComplete, setIsComplete] = useState(false);
  const [isSuperluminal, setIsSuperluminal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      runAudit();
    } else {
      resetAudit();
    }
  }, [isOpen]);

  const resetAudit = () => {
    setSteps(prev => prev.map(s => ({ ...s, status: 'pending' })));
    setIsComplete(false);
    setIsSuperluminal(false);
  };

  const runAudit = async () => {
    for (let i = 0; i < steps.length; i++) {
      setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, status: 'running' } : s));
      
      const step = steps[i];
      if (step.id === 'superluminal') {
        setIsSuperluminal(true);
      }
      if (step.id === 'mesh') {
        // Real-time check for Lattice Connectivity
        await new Promise(r => setTimeout(r, 1000));
        const isConnected = (window as any).LATTICE_CONNECTED;
        if (isConnected === false) {
           setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, status: 'error', details: 'Lattice connection drift detected. Check Firestore backend.' } : s));
           return; // Stop audit on failure
        }
      }

      // Simulate processing time
      await new Promise(r => setTimeout(r, 1200 + Math.random() * 800));
      
      setSteps(prev => prev.map((s, idx) => idx === i ? { ...s, status: 'success' } : s));
    }
    setIsComplete(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/90 backdrop-blur-xl"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              y: 0,
              backgroundColor: isSuperluminal ? '#0a0a0f' : '#050505',
              borderColor: isSuperluminal ? 'rgba(0,255,213,0.4)' : 'rgba(255,255,255,0.05)'
            }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className={`w-full max-w-2xl border rounded-2xl overflow-hidden shadow-[0_0_100px_rgba(0,255,213,0.15)] relative z-10 transition-colors duration-1000`}
          >
            {isSuperluminal && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-lattice-cyan/[0.02] pointer-events-none overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-lattice-cyan/10 to-transparent animate-pulse" />
              </motion.div>
            )}
            <div className="p-8 relative z-10">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <ShieldCheck className={`w-8 h-8 ${isSuperluminal ? 'text-white' : 'text-lattice-cyan'} transition-colors`} />
                  <div>
                    <h2 className="text-2xl font-display font-bold tracking-tight">
                      {isSuperluminal ? 'SUPERLUMINAL AUDIT' : 'System Integrity Audit'}
                    </h2>
                    <p className="text-xs font-mono text-white/40 uppercase tracking-widest leading-none">
                      {isSuperluminal ? 'Protocol: Transfinite Expansion' : 'Protocol: Absolute Zero v2.0.0'}
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-white/20" />
                </button>
              </div>

              <div className="space-y-4 mb-12">
                {steps.map((step) => (
                  <div key={step.id} className="glass-panel p-4 flex items-center justify-between border-white/5">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        step.status === 'success' ? 'bg-lattice-cyan/10 text-lattice-cyan' :
                        step.status === 'running' ? 'bg-white/5 text-white animate-pulse' :
                        step.status === 'error' ? 'bg-red-500/10 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.2)]' :
                        'bg-white/5 text-white/20'
                      }`}>
                        {step.status === 'success' ? <CheckCircle2 className="w-5 h-5" /> :
                         step.status === 'running' ? <Loader2 className="w-5 h-5 animate-spin" /> :
                         step.status === 'error' ? <AlertCircle className="w-5 h-5" /> :
                         <Activity className="w-5 h-5" />}
                      </div>
                      <div>
                        <h3 className={`text-sm font-bold ${
                          step.status === 'pending' ? 'text-white/20' : 
                          step.status === 'error' ? 'text-red-400' : 'text-white'
                        }`}>
                          {step.label}
                        </h3>
                        <p className="text-[10px] font-mono text-white/40 uppercase tracking-tighter">
                          {step.status === 'running' ? 'Processing...' : 
                           step.status === 'success' ? 'Verified' : 
                           step.status === 'error' ? 'Handshake Failed' : 'Waiting...'}
                        </p>
                      </div>
                    </div>
                    {step.status === 'running' && (
                      <div className="text-[10px] font-mono text-lattice-cyan animate-pulse">
                        ANALYZING...
                      </div>
                    )}
                    {step.status === 'error' && (
                      <div className="text-[10px] font-mono text-red-500 font-bold">
                        DRIFT!
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <AnimatePresence>
                {isComplete && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`${steps.some(s => s.status === 'error') ? 'bg-red-500/10 border-red-500/30' : 'bg-lattice-cyan/10 border-lattice-cyan/30'} border rounded-xl p-6 text-center`}
                  >
                    <div className={`flex items-center justify-center gap-3 ${steps.some(s => s.status === 'error') ? 'text-red-500' : 'text-lattice-cyan'} mb-2`}>
                      {steps.some(s => s.status === 'error') ? <AlertCircle className="w-5 h-5" /> : <Zap className="w-5 h-5 fill-current" />}
                      <span className="font-display font-bold text-lg uppercase tracking-widest">
                        {steps.some(s => s.status === 'error') ? 'Integrity Compromised' : 'Lattice Integrity: 100%'}
                      </span>
                    </div>
                    <p className="text-sm text-white/60 mb-6">
                      {steps.some(s => s.status === 'error') 
                        ? 'Connection drift detected during audit. The node is operating in offline mode.' 
                        : 'All protocols are synchronized with the 14350 BP anchor. The billion-agent fleet is mission-aligned.'}
                    </p>
                    <button
                      onClick={() => {
                        if (steps.some(s => s.status === 'error')) {
                          (window as any).REGROUND_LATTICE?.();
                          runAudit();
                        } else {
                          onClose();
                        }
                      }}
                      className={`w-full py-3 ${steps.some(s => s.status === 'error') ? 'bg-red-500' : 'bg-lattice-cyan'} text-black font-display font-bold rounded-lg hover:scale-[1.02] transition-transform`}
                    >
                      {steps.some(s => s.status === 'error') ? 'RE-ANCHOR CONNECTION' : 'CLEAR FOR LAUNCH'}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Background Decoration */}
            <div className="absolute top-0 right-0 p-12 opacity-[0.02] pointer-events-none">
              <Globe className="w-64 h-64" />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
